import { getDefaultOptionsValues, getOptionsConfig } from '@rawgraphs/rawgraphs-core'

/**
 * The minimum serializable state needed to render a Lugsail chart anywhere.
 *
 * Keeping this separate from React context lets Canvas render the same chart
 * contract as the production preview without inheriting the production UI.
 */
export function getDefaultChartVisualOptions(chart) {
  const options = getOptionsConfig(chart?.visualOptions)
  const defaults = getDefaultOptionsValues(options)

  // This is the production preview default, not a Canvas-specific choice.
  if (Object.prototype.hasOwnProperty.call(options, 'showGrid')) {
    defaults.showGrid = false
  }

  // A ranked bar chart is the more useful starting point for a new chart.
  // Saved/imported visual options still win when createChartSnapshot merges
  // them below, so this only affects newly created bar charts.
  if (
    chart?.metadata?.id === 'rawgraphs.barchart' &&
    Object.prototype.hasOwnProperty.call(options, 'sortBarsBy')
  ) {
    defaults.sortBarsBy = 'totalDescending'
  }

  return defaults
}

export function createChartSnapshot({ chart, mapping = {}, visualOptions, brandTheme }) {
  return {
    chartId: chart.metadata.id,
    mapping,
    visualOptions: {
      ...getDefaultChartVisualOptions(chart),
      ...(visualOptions || {}),
    },
    brandTheme,
  }
}

// Option IDs are not a compatible API by themselves: two charts can both
// expose `colorScale` while requiring different value shapes. Keep this list
// intentionally conservative until an option-level portability contract exists.
const PORTABLE_CANVAS_VISUAL_OPTIONS = new Set([
  'background',
  'marginTop',
  'marginRight',
  'marginBottom',
  'marginLeft',
])

/**
 * Keep portable presentation options when changing chart type. Renderer-
 * specific scales and layout controls always come from the new chart defaults.
 */
export function getCompatibleChartVisualOptions(chart, visualOptions = {}) {
  const definitions = getOptionsConfig(chart?.visualOptions)
  return Object.fromEntries(
    Object.entries(visualOptions).filter(([optionId]) =>
      PORTABLE_CANVAS_VISUAL_OPTIONS.has(optionId)
      && Object.prototype.hasOwnProperty.call(definitions, optionId)
    )
  )
}
